import { useEffect, useRef, useState, useMemo } from 'react';
import { List } from 'react-window';
import { useTranslation } from 'react-i18next';

import BenchmarkRange from '../../BenchmarkRange';
import ChartDataAlert from '../../ChartDataAlert';
import ChartXAxisLabel from './ChartXAxisLabel';
import ChartYAxis from './ChartYAxis';
import BenchmarkTooltip from '../BenchmarkTooltip';

import {
  getAllBenchmarkDetails,
  getLabelAndColor,
} from '../../../../../utilities/common/utils';
import { useArrayRef } from '../../../../../utilities/common/hooks/useRefArray';
import {
  BarChartMetaData,
  WidgetDefinition,
} from '../../../../../shared/types/widget';
import useFilters from '../../../../../utilities/hooks/useFilters';
import { useFilterData } from '../../../../../utilities/hooks/useFilterData';
import { ChartDatasetIndex } from '../../../../../shared/constants/chart';

import { bottomOffsetClass } from './getBottomOffsetClass';
import Row, { type TooltipData } from './Row';
import Switch from '../../../../widgets/Switch';

interface BarChartProps {
  definition: WidgetDefinition;
  content: BarChartMetaData;
}

function BarChartHorizontalRich({ definition, content }: BarChartProps) {
  const [isBenchmarkEnabled, setIsBenchmarkEnabled] = useState(true);
  const [showRespondentsLine, setShowRespondentsLine] = useState(true);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const barRefs = useArrayRef<HTMLButtonElement>();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipHeight, setTooltipHeight] = useState(0);
  const hoverTimeoutRef = useRef<number | null>(null);

  const { title } = definition;

  const { benchmarkType } = useFilters();
  const { configData } = useFilterData();
  const { t } = useTranslation();

  // Remove tooltip if scroll
  useEffect(() => {
    const handleScroll = () => {
      if (tooltip) {
        setTooltip(null);
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [tooltip]);

  useEffect(() => {
    if (tooltip && tooltipRef.current) {
      setTooltipHeight(tooltipRef.current.offsetHeight);
    }
  }, [tooltip]);

  const bars = useMemo(() => {
    if (!content?.data) return [];
    return content.data.labels.map((label, idx) => {
      const score =
        content.data.datasets?.[ChartDatasetIndex.DATA_INDEX]?.data?.[idx] ?? 0;
      const uniqueRespondents =
        content.data.datasets?.[ChartDatasetIndex.RESPONDENT_INDEX]?.data?.[
          idx
        ] ?? 0;
      const colorObj = getLabelAndColor(score, content?.meta.scoreRanges ?? []);
      return { label, score, uniqueRespondents, color: colorObj?.color };
    });
  }, [content]);

  const barsSorted = useMemo(() => {
    // Exclude row if value -1 (less than 5 users)
    const filteredBars = bars.filter((bar) => bar.score !== -1);
    return filteredBars.toSorted((a, b) => b.score - a.score);
  }, [bars]);

  const ChartContent = useMemo(() => {
    const benchmarkDetails = getAllBenchmarkDetails(
      content?.meta,
      benchmarkType,
      configData!,
    );

    // Flag to check if there are any value -1 (less than 5 users)
    // - Note: Here we are comparing against score, but actually it's against uniqueRespondents.
    // - Both will return same value for case -1
    const hasInvalidScores = bars.some((bar) => bar.score === -1);

    const isChartScrollable = barsSorted.length > 10;
    const bottomOffset = bottomOffsetClass(isChartScrollable);

    const MAX_HEIGHT = 484;
    const itemCount = barsSorted.length;
    const itemSize = 48;
    const listHeight = Math.min(itemCount * itemSize, MAX_HEIGHT);

    return (
      <>
        <div
          className="mt-3 mb-6 flex w-full flex-row items-center justify-center gap-8"
          data-html2canvas-ignore="true"
        >
          <Switch
            label={t('BENCHMARKS')}
            checked={isBenchmarkEnabled}
            onChange={(checked) => setIsBenchmarkEnabled(checked)}
          />

          <Switch
            label={`${t('UNIQUE_RESPONDENTS')}`}
            checked={showRespondentsLine}
            onChange={(checked) => setShowRespondentsLine(checked)}
          />
        </div>
        <ChartDataAlert
          show={hasInvalidScores}
          title={t('HORIZONTAL_CHART_DATA_ALERT_TITLE')}
          subTitle={t('HORIZONTAL_CHART_DATA_ALERT_DESCRIPTION')}
        />
        <div className="relative mb-5 w-full overflow-auto px-2 lg:mb-0 lg:px-0">
          <div className="m-auto flex max-w-[1208px] min-w-[700px] flex-col gap-y-6 px-4 py-6 pt-10">
            <div className="relative">
              <div className="relative z-[2] max-h-[600px] overflow-auto">
                <div className="sticky top-0 flex items-center gap-x-4">
                  <span className="text-secondary-blue-700 w-[131px] truncate text-right text-xs font-medium">
                    {title}
                  </span>

                  <div className="w-full" />
                  <div className="text-secondary-blue-700 w-[180px] truncate text-left text-xs font-medium">
                    {showRespondentsLine &&
                      content?.data?.datasets?.[
                        ChartDatasetIndex.RESPONDENT_INDEX
                      ]?.label}
                  </div>
                </div>
                <div
                  style={{ height: listHeight }}
                  className={barsSorted.length === 8 ? '!overflow-hidden' : ''}
                >
                  <List
                    rowComponent={Row}
                    rowCount={barsSorted.length}
                    rowHeight={48}
                    rowProps={{
                      barsSorted,
                      showRespondentsLine,
                      barRefs,
                      setTooltip,
                      hoverTimeoutRef,
                    }}
                  />
                </div>
              </div>

              <ChartXAxisLabel isChartScrollable={isChartScrollable} />

              <ChartYAxis
                bottomOffset={bottomOffset}
                isBenchmarkEnabled={isBenchmarkEnabled}
                benchmarkDetails={benchmarkDetails}
                isChartScrollable={isChartScrollable}
              />
            </div>
            <div className="mt-8 flex justify-between">
              <span className="w-[131px]" />
              <div className="text-secondary-blue-700 text-xs font-medium">
                {content?.data?.datasets?.[ChartDatasetIndex.DATA_INDEX]?.label}
              </div>
              <span className="w-[180px]" />
            </div>
            <div className="mt-6 mb-3 flex px-5 lg:px-15">
              <span className="min-w-24 font-medium">Score:</span>
              <BenchmarkRange meta={content?.meta} className="gap-x-6" />
            </div>
          </div>
        </div>
      </>
    );
  }, [
    showRespondentsLine,
    isBenchmarkEnabled,
    benchmarkType,
    configData,
    content,
  ]);

  return (
    <>
      {ChartContent}

      {tooltip && (
        <BenchmarkTooltip
          tooltip={tooltip}
          tooltipHeight={tooltipHeight}
          tooltipRef={tooltipRef}
          meta={content}
          setTooltip={setTooltip}
          hoverTimeoutRef={hoverTimeoutRef}
        />
      )}
    </>
  );
}

export default BarChartHorizontalRich;
